import { isNil } from 'lodash';
import { Editor, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';
import { MARK_TEXT_TRANSFORM } from '../../BlogEditor/plugins/TextTransform/types';
import { getEmptyParagraphNode } from '@doltech/utils/lib/editor';
import { htmlToSlate } from '../deserializers/html-deserializer/withDeserializeHTML';

export type EditorType = Editor & ReactEditor;

export const isBlockActive = (editor: EditorType, format: any) => {
  const [match] = Editor.nodes(editor, {
    match: (n: any) => n.type === format,
  });
  return !!match;
};

export const toggleBlock = (editor: EditorType, format: any) => {
  const isActive = isBlockActive(editor, format);

  Transforms.setNodes(editor, {
    type: isActive ? 'paragraph' : format,
  } as any);

  if (!isActive) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

export const isMarkActive = (editor: EditorType, format: any, value?: any) => {
  const marks = Editor.marks(editor);
  if (marks) {
    if (isNil(value)) {
      return marks[format] === true;
    }
    if (typeof value === 'function') {
      return value(marks);
    }
    return marks[format] === value;
  }
  return false;
};

export const getCurrentMark = (editor: EditorType, format: any, value?: any) => {
  const nodes = Array.from(
    Editor.nodes(editor, {
      match: (item: any) => Boolean(item[format]),
      mode: 'all',
    })
  );
  return nodes && nodes[0] ? nodes[0][0] : undefined;
};

export const toggleMark = (editor: EditorType, format: any, value?: any) => {
  const isActive = isMarkActive(editor, format, value);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    if (isNil(value)) {
      Editor.addMark(editor, format, true);
      return;
    }
    Editor.addMark(editor, format, value);
  }
};

export const isBlockType = (node, type) => node.type === type;

export const getCurrentMarkLeafAtSelection = (editor, format) => {
  const { selection } = editor;
  const nodeEntries = Editor.nodes(editor, { at: selection });
  let leafs = [];
  if (selection) {
    for (const [node, path] of nodeEntries) {
      if (node?.[format]) {
        leafs = [node as any, path];
      }
    }
  }
  return leafs;
};

export const toggleTextTransformMark = (editor, markKey) => {
  if (editor.selection) {
    const markLeafs = getCurrentMarkLeafAtSelection(editor, MARK_TEXT_TRANSFORM);
    if (markLeafs) {
      const isActive = markLeafs.some((markLeaf) => Boolean(markLeaf?.[MARK_TEXT_TRANSFORM]));
      const isValueEqual = markLeafs.some(
        (markLeaf) =>
          Boolean(markLeaf?.[MARK_TEXT_TRANSFORM]) && markLeaf[MARK_TEXT_TRANSFORM] === markKey
      );
      if (isActive) {
        Editor.removeMark(editor, MARK_TEXT_TRANSFORM);
      }
      if (isValueEqual) {
        return;
      }
    }
    Editor.addMark(editor, MARK_TEXT_TRANSFORM, markKey);
  }
};

export const toggleMarkColor = (editor, markKey, colorKey) => {
  if (editor?.selection) {
    const markLeafs = getCurrentMarkLeafAtSelection(editor, markKey);
    if (markLeafs) {
      const isActive = markLeafs.some(
        (markLeaf) => Boolean(markLeaf?.[markKey]) && markLeaf[markKey]?.colorKey === colorKey
      );
      if (isActive) {
        Editor.removeMark(editor, markKey);
        return;
      }
    }
    Editor.addMark(editor, markKey, { colorKey });
  }
};

export const getInitialValueWithHtmlCheck = (value, plugins = []) => {
  let result = value;
  if (value?.length === 0) {
    result = [getEmptyParagraphNode()];
  }
  if (typeof result === 'boolean' || result === 'true' || result === 'false') {
    return [getEmptyParagraphNode(result.toString())];
  }
  if (typeof result === 'string') {
    try {
      result = JSON.parse(result);
    } catch (e) {
      return htmlToSlate(plugins, result);
    }
  }
  return result;
};
